import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  ArrowLeft,
  FileText,
  FileSpreadsheet,
  FileDown,
  Calendar,
  Briefcase,
  Gift,
  Layers,
  DollarSign,
  MessageCircle,
  Loader2,
  ArrowRightLeft,
  Pencil,
  Trash2,
  Sparkles,
  User,
  Phone,
  MapPin,
  X,
  SearchX,
} from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { getApiErrorMessage } from "../../atoms/Utlis/getApiErrorMessage";
import {
  getQuotationByIdAPI,
  shareQuotationOnWhatsAppAPI,
  convertQuotationToOrderAPI,
  deleteQuotationAPI,
} from "../../../Redux/ApiCalls/Dashboard/QuotationAPI";
import DeletePopup from "../../atoms/Dashboard/Folder/DeletePopup";
import { useAppSelector } from "../../../Redux/Hooks";
import apiCall from "../../../Redux/ApiCalls/AuthorizedApi";
import { DocumentPrintTemplate } from "./DocumentPrintTemplate";
import { formatTimeToAMPM } from "../../../Utils/helper";
import { exportElementAsPDF } from "../../../Utils/pdfHelpers";
import QuotationShareModal from "./QuotationShareModal";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "./Marketplace/MarketplaceEmptyState";
import { StatusBadge } from "./documentSharedStyles";
import {
  TableWrap,
  PaymentsTable,
  AmountCell,
} from "../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroRow as ModalHeroRow,
  HeroText as ModalHeroText,
  HeroBadge as ModalHeroBadge,
  ModalTitle as ConfirmModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
} from "../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import { device } from "../../atoms/Utlis/media";
import PermissionGate from "../../atoms/Utlis/PermissionGate";

const QuotationDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [quotation, setQuotation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sharingWhatsApp, setSharingWhatsApp] = useState(false);
  const [modalShareQuotation, setModalShareQuotation] = useState<any>(null);
  const [convertConfirm, setConvertConfirm] = useState(false);
  const [converting, setConverting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const calculatedGrandTotal = useMemo(() => {
    if (!quotation) return 0;
    if (quotation.without_price) return 0;
    let resourcesSum = 0;
    if (quotation.resources) {
      resourcesSum = quotation.resources.reduce(
        (sum: number, r: any) =>
          sum + (Number(r.unit_price) || 0) * (Number(r.quantity) || 0),
        0
      );
    }
    let fixChargesSum = 0;
    if (quotation.fix_charge_items) {
      fixChargesSum = quotation.fix_charge_items.reduce(
        (sum: number, fc: any) =>
          sum + (Number(fc.unit_price) || 0) * (Number(fc.quantity) || 0),
        0
      );
    }
    const totalAmount = resourcesSum + fixChargesSum;
    const discountAmount =
      (totalAmount * (Number(quotation.discount_percent) || 0)) / 100;
    const subtotal = totalAmount - discountAmount;
    const taxAmount = (subtotal * (Number(quotation.tax_percent) || 0)) / 100;
    return subtotal + taxAmount;
  }, [quotation]);

  const { user, studio_logo, business } = useAppSelector((state) => state.auth);

  const queryParams = new URLSearchParams(location.search);
  const qId = queryParams.get("id");

  useEffect(() => {
    const fetchDetails = async () => {
      if (qId) {
        try {
          const res = await getQuotationByIdAPI(qId);
          setQuotation(res);
        } catch {
          showErrorToast("Failed to fetch quotation details");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [qId]);

  const handleExportCSV = () => {
    if (!quotation) return;
    let csv = `Quotation Details\n`;
    csv += `Quotation No,${quotation.quotation_no}\n`;
    csv += `Date,${quotation.quotation_date}\n`;
    csv += `Customer Name,${quotation.customer_name}\n`;
    csv += `Customer Phone,${quotation.customer_phone}\n`;
    csv += `Place,${quotation.customer_place || ""}\n\n`;

    if (quotation.events && quotation.events.length > 0) {
      const hasAnyDate = quotation.events.some((e: any) => Boolean(e.event_date && String(e.event_date).trim()));
      const hasAnyTime = quotation.events.some((e: any) => Boolean(formatTimeToAMPM(e.event_time)));
      csv += `EVENTS\n`;
      const eventHeaders: string[] = [];
      if (hasAnyDate) eventHeaders.push("Date");
      if (hasAnyTime) eventHeaders.push("Time");
      eventHeaders.push("Event Type");
      csv += `${eventHeaders.join(",")}\n`;
      quotation.events.forEach((e: any) => {
        const row: string[] = [];
        if (hasAnyDate) row.push(`"${e.event_date || ""}"`);
        if (hasAnyTime) row.push(`"${formatTimeToAMPM(e.event_time)}"`);
        row.push(`"${e.event_type_name || ""}"`);
        csv += `${row.join(",")}\n`;
      });
      csv += `\n`;
    }

    if (quotation.resources && quotation.resources.length > 0) {
      csv += `RESOURCES / PRODUCTS\n`;
      csv += `Resource Name,Quantity,Price,Total\n`;
      quotation.resources.forEach((r: any) => {
        csv += `"${r.resource_name || ""}","${r.quantity}","${r.unit_price}","${(r.quantity * r.unit_price).toFixed(2)}"\n`;
      });
      csv += `\n`;
    }

    if (quotation.fix_charge_items && quotation.fix_charge_items.length > 0) {
      csv += `FIXED CHARGES\n`;
      csv += `Charge Name,Quantity,Price,Total\n`;
      quotation.fix_charge_items.forEach((fc: any) => {
        csv += `"${fc.charge_name || ""}","${fc.quantity}","${fc.unit_price}","${(fc.quantity * fc.unit_price).toFixed(2)}"\n`;
      });
      csv += `\n`;
    }

    if (quotation.album_detail) {
      csv += `ALBUM DETAILS\n`;
      csv += `Album Type,${quotation.album_detail.album_type_name || ""}\n`;
      csv += `Width x Height,${quotation.album_detail.album_width || ""} x ${quotation.album_detail.album_height || ""} inches\n`;
      csv += `Album Paper,${quotation.album_detail.album_paper_name || ""}\n`;
      csv += `Album Pages,${quotation.album_detail.album_pages || ""}\n`;
      csv += `\n`;
    }

    if (quotation.product_info_description) {
      const escapedNotes = `"${quotation.product_info_description.replace(/"/g, '""')}"`;
      csv += `PRODUCT INFORMATION / DESCRIPTION NOTES\n`;
      csv += `${escapedNotes}\n`;
      csv += `\n`;
    }

    if (quotation.optional_services) {
      const opts = quotation.optional_services;
      const hasOpts =
        opts.opt_album_page_cost ||
        Number(opts.opt_album_page_price) > 0 ||
        opts.opt_video_cost ||
        Number(opts.opt_video_price) > 0 ||
        Number(opts.opt_pad_price_from) > 0 ||
        Number(opts.opt_pad_price_to) > 0;
      if (hasOpts) {
        csv += `OPTIONAL SERVICES\n`;
        csv += `Service,Details / Price\n`;
        if (opts.opt_album_page_cost || Number(opts.opt_album_page_price) > 0) {
          const priceStr =
            Number(opts.opt_album_page_price) > 0
              ? `₹${opts.opt_album_page_price}`
              : "-";
          csv += `"Album Extra Page Sheet: ${opts.opt_album_page_cost || "Extra Sheet"}","${priceStr}"\n`;
        }
        if (opts.opt_video_cost || Number(opts.opt_video_price) > 0) {
          const priceStr =
            Number(opts.opt_video_price) > 0 ? `₹${opts.opt_video_price}` : "-";
          csv += `"Extra Video: ${opts.opt_video_cost || "Extra Video"}","${priceStr}"\n`;
        }
        if (
          Number(opts.opt_pad_price_from) > 0 ||
          Number(opts.opt_pad_price_to) > 0
        ) {
          csv += `"Album Pad Price Range","₹${opts.opt_pad_price_from || 0} TO ₹${opts.opt_pad_price_to || 0}"\n`;
        }
        csv += `\n`;
      }
    }

    csv += `PRICING\n`;
    csv += `Discount %,${quotation.discount_percent || 0}\n`;
    csv += `Tax %,${quotation.tax_percent || 0}\n`;
    csv += `Grand Total,₹${calculatedGrandTotal.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Quotation_${quotation.quotation_no}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showSuccessToast("CSV exported successfully");
  };

  const handleExportExcel = () => {
    if (!quotation) return;
    let xls = `Quotation Details\t${quotation.quotation_no}\n`;
    xls += `Date\t${quotation.quotation_date}\n`;
    xls += `Customer Name\t${quotation.customer_name}\n`;
    xls += `Customer Phone\t${quotation.customer_phone}\n`;
    xls += `Place\t${quotation.customer_place || ""}\n\n`;

    if (quotation.events && quotation.events.length > 0) {
      const hasAnyDate = quotation.events.some((e: any) => Boolean(e.event_date && String(e.event_date).trim()));
      const hasAnyTime = quotation.events.some((e: any) => Boolean(formatTimeToAMPM(e.event_time)));
      xls += `EVENTS\n`;
      const eventHeaders: string[] = [];
      if (hasAnyDate) eventHeaders.push("Date");
      if (hasAnyTime) eventHeaders.push("Time");
      eventHeaders.push("Event Type");
      xls += `${eventHeaders.join("\t")}\n`;
      quotation.events.forEach((e: any) => {
        const row: string[] = [];
        if (hasAnyDate) row.push(e.event_date || "");
        if (hasAnyTime) row.push(formatTimeToAMPM(e.event_time));
        row.push(e.event_type_name || "");
        xls += `${row.join("\t")}\n`;
      });
      xls += `\n`;
    }

    if (quotation.resources && quotation.resources.length > 0) {
      xls += `RESOURCES / PRODUCTS\n`;
      xls += `Resource Name\tQuantity\tPrice\tTotal\n`;
      quotation.resources.forEach((r: any) => {
        xls += `${r.resource_name || ""}\t${r.quantity}\t${r.unit_price}\t${(r.quantity * r.unit_price).toFixed(2)}\n`;
      });
      xls += `\n`;
    }

    if (quotation.fix_charge_items && quotation.fix_charge_items.length > 0) {
      xls += `FIXED CHARGES\n`;
      xls += `Charge Name\tQuantity\tPrice\tTotal\n`;
      quotation.fix_charge_items.forEach((fc: any) => {
        xls += `${fc.charge_name || ""}\t${fc.quantity}\t${fc.unit_price}\t${(fc.quantity * fc.unit_price).toFixed(2)}\n`;
      });
      xls += `\n`;
    }

    if (quotation.album_detail) {
      xls += `ALBUM DETAILS\n`;
      xls += `Album Type\t${quotation.album_detail.album_type_name || ""}\n`;
      xls += `Width x Height\t${quotation.album_detail.album_width || ""} x ${quotation.album_detail.album_height || ""} inches\n`;
      xls += `Album Paper\t${quotation.album_detail.album_paper_name || ""}\n`;
      xls += `Album Pages\t${quotation.album_detail.album_pages || ""}\n\n`;
    }

    if (quotation.product_info_description) {
      xls += `PRODUCT INFORMATION / DESCRIPTION NOTES\n`;
      xls += `"${quotation.product_info_description.replace(/"/g, '""')}"\n\n`;
    }

    if (quotation.optional_services) {
      const opts = quotation.optional_services;
      const hasOpts =
        opts.opt_album_page_cost ||
        Number(opts.opt_album_page_price) > 0 ||
        opts.opt_video_cost ||
        Number(opts.opt_video_price) > 0 ||
        Number(opts.opt_pad_price_from) > 0 ||
        Number(opts.opt_pad_price_to) > 0;
      if (hasOpts) {
        xls += `OPTIONAL SERVICES\n`;
        xls += `Service\tDetails / Price\n`;
        if (opts.opt_album_page_cost || Number(opts.opt_album_page_price) > 0) {
          const priceStr =
            Number(opts.opt_album_page_price) > 0
              ? `₹${opts.opt_album_page_price}`
              : "-";
          xls += `Album Extra Page Sheet: ${opts.opt_album_page_cost || "Extra Sheet"}\t${priceStr}\n`;
        }
        if (opts.opt_video_cost || Number(opts.opt_video_price) > 0) {
          const priceStr =
            Number(opts.opt_video_price) > 0 ? `₹${opts.opt_video_price}` : "-";
          xls += `Extra Video: ${opts.opt_video_cost || "Extra Video"}\t${priceStr}\n`;
        }
        if (
          Number(opts.opt_pad_price_from) > 0 ||
          Number(opts.opt_pad_price_to) > 0
        ) {
          xls += `Album Pad Price Range\t₹${opts.opt_pad_price_from || 0} TO ₹${opts.opt_pad_price_to || 0}\n`;
        }
        xls += `\n`;
      }
    }

    xls += `PRICING\n`;
    xls += `Discount %\t${quotation.discount_percent || 0}\n`;
    xls += `Tax %\t${quotation.tax_percent || 0}\n`;
    xls += `Grand Total\t₹${calculatedGrandTotal.toFixed(2)}\n`;

    const blob = new Blob([xls], { type: "application/vnd.ms-excel" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Quotation_${quotation.quotation_no}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showSuccessToast("Excel exported successfully");
  };

  const generatePdfBlob = async (): Promise<{ blob: Blob; filename: string }> => {
    const element = printRef.current;
    if (!element) throw new Error("Print template not ready");
    const blob = await exportElementAsPDF(element);
    const filename = `Quotation_${quotation.quotation_no}.pdf`;
    return { blob, filename };
  };

  const handleExportPDF = async () => {
    if (!quotation) return;
    try {
      const { blob, filename } = await generatePdfBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccessToast("PDF exported successfully");
    } catch {
      showErrorToast("Failed to generate PDF");
    }
  };

  const handleShareWhatsApp = async () => {
    if (!quotation) return;
    setModalShareQuotation({
      ...quotation,
      grand_total: String(calculatedGrandTotal),
    });
  };

  const handleOfficialShare = async () => {
    if (!quotation) return;
    setSharingWhatsApp(true);
    try {
      const { blob, filename } = await generatePdfBlob();
      const result = await shareQuotationOnWhatsAppAPI(
        quotation.id,
        blob,
        filename
      );
      setQuotation((prev: any) => ({
        ...prev,
        status: result.status || "sent",
      }));
      showSuccessToast(
        `Quotation sent to ${result.sent_to || quotation.customer_phone} on WhatsApp 🎉`
      );
    } catch (err: any) {
      showErrorToast(
        err?.message || "Failed to share on WhatsApp. Please try again."
      );
    } finally {
      setSharingWhatsApp(false);
      setModalShareQuotation(null);
    }
  };

  const handleViewConvertedOrder = async () => {
    if (!quotation) return;
    try {
      let res = await apiCall({
        method: "GET",
        url: `/order/orders/?search=${quotation.quotation_no}`,
      });
      let orders = res?.results || res || [];
      let matchedOrder = orders.find(
        (o: any) =>
          o.quotation === quotation.id ||
          String(o.quotation_id) === String(quotation.id) ||
          o.quotation_no === quotation.quotation_no ||
          o.reference_no === quotation.quotation_no ||
          o.order_no === quotation.quotation_no
      );

      if (!matchedOrder && quotation.customer_phone) {
        res = await apiCall({
          method: "GET",
          url: `/order/orders/?search=${quotation.customer_phone}`,
        });
        const phoneOrders = res?.results || res || [];
        matchedOrder = phoneOrders.find(
          (o: any) =>
            o.quotation === quotation.id ||
            String(o.quotation_id) === String(quotation.id) ||
            o.quotation_no === quotation.quotation_no ||
            o.reference_no === quotation.quotation_no ||
            o.order_no === quotation.quotation_no ||
            o.customer_phone === quotation.customer_phone ||
            o.customer_details?.phone === quotation.customer_phone
        );
      }

      if (matchedOrder && matchedOrder.id) {
        navigate(`/dashboard/orders/view?id=${matchedOrder.id}`);
      } else {
        showErrorToast("The converted order has been deleted.");
        setQuotation((prev: any) => ({ ...prev, status: "draft" }));
      }
    } catch {
      showErrorToast("Failed to locate converted order");
    }
  };

  const handleConvertToOrder = async () => {
    if (!quotation) return;
    if (quotation.status === "accepted" || quotation.status === "converted") {
      await handleViewConvertedOrder();
      return;
    }
    setConvertConfirm(true);
  };

  const handleConfirmConvert = async () => {
    if (!quotation) return;
    setConverting(true);
    try {
      const response = await convertQuotationToOrderAPI(quotation.id);
      showSuccessToast("Quotation converted to Order successfully!");
      const newOrderId =
        response?.id || response?.order_id || response?.order?.id;
      if (newOrderId) {
        navigate(`/dashboard/orders/view?id=${newOrderId}`);
      } else {
        navigate("/dashboard/orders/all");
      }
    } catch (err: any) {
      showErrorToast(
        getApiErrorMessage(err, "Failed to convert quotation to order")
      );
    } finally {
      setConverting(false);
      setConvertConfirm(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!quotation) return;
    setDeleting(true);
    try {
      await deleteQuotationAPI(quotation.id);
      showSuccessToast("Quotation deleted successfully");
      navigate("/dashboard/quotation/");
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to delete quotation"));
    } finally {
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <MarketplaceLoadingSpinner padding="80px 0" />
      </PageContainer>
    );
  }

  if (!quotation) {
    return (
      <PageContainer>
        <Hero>
          <HeroGlow />
          <HeroContent>
            <HeroLeft>
              <BackBtn
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <ArrowLeft size={18} />
              </BackBtn>
              <HeroText>
                <HeroBadge>
                  <Sparkles size={13} />
                  Quotation
                </HeroBadge>
                <PageTitle className="linear-text">Quotation Details</PageTitle>
              </HeroText>
            </HeroLeft>
          </HeroContent>
        </Hero>
        <MarketplaceEmptyState
          icon={<SearchX size={36} strokeWidth={1.5} />}
          title="No quotation data found"
          description="This quotation may have been deleted or the link is invalid."
          actions={[
            {
              label: "Back to quotations",
              onClick: () => navigate("/dashboard/quotation/"),
            },
          ]}
        />
      </PageContainer>
    );
  }

  const phoneIsMissing = !quotation.customer_phone?.trim();
  const whatsappTooltip = phoneIsMissing
    ? "Add customer phone number before sharing on WhatsApp"
    : "Share quotation PDF on WhatsApp";
  const isAlreadyConverted =
    quotation.status === "accepted" || quotation.status === "converted";

  const hasOptionalServices =
    quotation.optional_services &&
    (quotation.optional_services.opt_album_page_cost ||
      quotation.optional_services.opt_album_page_price > 0 ||
      quotation.optional_services.opt_video_cost ||
      quotation.optional_services.opt_video_price > 0 ||
      quotation.optional_services.opt_pad_price_from > 0 ||
      quotation.optional_services.opt_pad_price_to > 0);

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackBtn
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <ArrowLeft size={18} />
            </BackBtn>
            <HeroText>
              <HeroBadge>
                <Sparkles size={13} />
                Quotation {quotation.quotation_no}
              </HeroBadge>
              <TitleRow>
                <PageTitle className="linear-text">Quotation Details</PageTitle>
                {quotation.status && (
                  <StatusBadge $status={quotation.status}>
                    {quotation.status}
                  </StatusBadge>
                )}
              </TitleRow>
              <PageSubtitle>
                View, export, and share details for {quotation.customer_name}.
              </PageSubtitle>
            </HeroText>
          </HeroLeft>
        </HeroContent>
      </Hero>

      <ScrollBody>
        <SectionCard>
          <ActionRow>
            <ActionGroup>
              <SoftBtn $tone="#35b7c8" type="button" onClick={handleExportCSV}>
                <FileText size={15} strokeWidth={2.2} />
                CSV
              </SoftBtn>
              <SoftBtn $tone="#31a66a" type="button" onClick={handleExportExcel}>
                <FileSpreadsheet size={15} strokeWidth={2.2} />
                Excel
              </SoftBtn>
              <SoftBtn $tone="#e44862" type="button" onClick={handleExportPDF}>
                <FileDown size={15} strokeWidth={2.2} />
                PDF
              </SoftBtn>
            </ActionGroup>

            <ActionDivider />

            <ActionGroup>
              <SoftBtn
                $tone="#25d366"
                type="button"
                disabled={phoneIsMissing || sharingWhatsApp}
                onClick={handleShareWhatsApp}
                title={whatsappTooltip}
              >
                {sharingWhatsApp ? (
                  <SpinIcon size={15} />
                ) : (
                  <MessageCircle size={15} strokeWidth={2.2} />
                )}
                {sharingWhatsApp ? "Sending..." : "WhatsApp"}
              </SoftBtn>
              <PermissionGate anyPermission={isAlreadyConverted ? ["orders.read"] : ["orders.create"]}>
                <SoftBtn
                  $tone={isAlreadyConverted ? "#10b981" : "#6366f1"}
                  type="button"
                  onClick={handleConvertToOrder}
                >
                  <ArrowRightLeft size={15} strokeWidth={2.2} />
                  {isAlreadyConverted ? "View Order" : "Convert to Order"}
                </SoftBtn>
              </PermissionGate>
            </ActionGroup>

            <ActionSpacer />

            <ActionGroup>
              <PermissionGate permission="quotation.update">
                <SoftBtn
                  $tone="#c94daa"
                  type="button"
                  onClick={() =>
                    navigate(`/dashboard/quotation/singleQuotation?id=${quotation.id}`)
                  }
                >
                  <Pencil size={15} strokeWidth={2.2} />
                  Edit
                </SoftBtn>
              </PermissionGate>
              <PermissionGate permission="quotation.delete">
                <SoftBtn
                  $tone="#e44862"
                  $solid
                  type="button"
                  onClick={() => setDeleteConfirm(true)}
                >
                  <Trash2 size={15} strokeWidth={2.2} />
                  Delete
                </SoftBtn>
              </PermissionGate>
            </ActionGroup>
          </ActionRow>

          <CardHeader>
            <CardIcon $tone="#c94daa">
              <Briefcase size={18} />
            </CardIcon>
            <div>
              <CardTitle>Basic Information</CardTitle>
              <CardSubtitle>Customer and quotation metadata</CardSubtitle>
            </div>
          </CardHeader>

          <InfoGrid>
            <InfoItem>
              <InfoLabel>
                <User size={12} /> Customer Name
              </InfoLabel>
              <InfoValue>{quotation.customer_name}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>
                <Phone size={12} /> Mobile Number
              </InfoLabel>
              <InfoValue>{quotation.customer_phone || "—"}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>
                <MapPin size={12} /> Place
              </InfoLabel>
              <InfoValue>{quotation.customer_place || "—"}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>
                <Calendar size={12} /> Quotation Date
              </InfoLabel>
              <InfoValue>{quotation.quotation_date}</InfoValue>
            </InfoItem>
          </InfoGrid>
        </SectionCard>

        {quotation.events && quotation.events.length > 0 && (() => {
          const hasAnyEventDate = quotation.events.some((e: any) => Boolean(e.event_date && String(e.event_date).trim()));
          const hasAnyEventTime = quotation.events.some((e: any) => Boolean(formatTimeToAMPM(e.event_time)));
          return (
            <SectionCard>
              <CardHeader>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CardIcon style={{ background: "#EEF2FF", color: "#4F46E5" }}>
                    <Calendar size={18} />
                  </CardIcon>
                  <CardTitle>Event Details</CardTitle>
                </div>
                <CardSubtitle>
                  {quotation.events.length} event
                  {quotation.events.length !== 1 ? "s" : ""} scheduled
                </CardSubtitle>
              </CardHeader>
              <TableWrap>
                <PaymentsTable $minWidth={520}>
                  <thead>
                    <tr>
                      {hasAnyEventDate && (
                        <th>
                          <span>Event Date</span>
                        </th>
                      )}
                      {hasAnyEventTime && (
                        <th>
                          <span>Event Time</span>
                        </th>
                      )}
                      <th>
                        <span>Event Type</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotation.events.map((e: any, idx: number) => {
                      const hasDate = Boolean(e.event_date && String(e.event_date).trim());
                      const formattedTime = formatTimeToAMPM(e.event_time);
                      return (
                        <tr key={idx}>
                          {hasAnyEventDate && <td>{hasDate ? e.event_date : ""}</td>}
                          {hasAnyEventTime && <td>{formattedTime || ""}</td>}
                          <td>{e.event_type_name || ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </PaymentsTable>
              </TableWrap>
            </SectionCard>
          );
        })()}

        {quotation.resources && quotation.resources.length > 0 && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#f09a3e">
                <Layers size={18} />
              </CardIcon>
              <div>
                <CardTitle>Resources & Products</CardTitle>
                <CardSubtitle>Line items included in this quote</CardSubtitle>
              </div>
            </CardHeader>
            <TableWrap>
              <PaymentsTable $minWidth={640}>
                <thead>
                  <tr>
                    <th>
                      <span>Resource Name</span>
                    </th>
                    <th>
                      <span>Type</span>
                    </th>
                    <th>
                      <span>Qty</span>
                    </th>
                    {!quotation.without_price && (
                      <>
                        <th>
                          <span>Unit Price</span>
                        </th>
                        <th>
                          <span>Amount</span>
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {quotation.resources.map((r: any, idx: number) => (
                    <tr key={idx}>
                      <td>{r.resource_name}</td>
                      <td style={{ textTransform: "capitalize" }}>
                        {r.resource_type}
                      </td>
                      <td>{r.quantity}</td>
                      {!quotation.without_price && (
                        <>
                          <td>
                            <AmountCell>
                              ₹
                              {Number(r.unit_price).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </AmountCell>
                          </td>
                          <td>
                            <AmountCell>
                              ₹
                              {(
                                r.quantity * Number(r.unit_price)
                              ).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </AmountCell>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </PaymentsTable>
            </TableWrap>
          </SectionCard>
        )}

        {quotation.fix_charge_items &&
          quotation.fix_charge_items.length > 0 && (
            <SectionCard>
              <CardHeader>
                <CardIcon $tone="#6366f1">
                  <DollarSign size={18} />
                </CardIcon>
                <div>
                  <CardTitle>Fixed Charges</CardTitle>
                  <CardSubtitle>Additional fixed line items</CardSubtitle>
                </div>
              </CardHeader>
              <TableWrap>
                <PaymentsTable $minWidth={520}>
                  <thead>
                    <tr>
                      <th>
                        <span>Charge Name</span>
                      </th>
                      <th>
                        <span>Qty</span>
                      </th>
                      {!quotation.without_price && (
                        <>
                          <th>
                            <span>Unit Price</span>
                          </th>
                          <th>
                            <span>Amount</span>
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {quotation.fix_charge_items.map(
                      (fc: any, idx: number) => (
                        <tr key={idx}>
                          <td>{fc.charge_name}</td>
                          <td>{fc.quantity}</td>
                          {!quotation.without_price && (
                            <>
                              <td>
                                <AmountCell>
                                  ₹
                                  {Number(fc.unit_price).toLocaleString(
                                    "en-IN",
                                    { minimumFractionDigits: 2 }
                                  )}
                                </AmountCell>
                              </td>
                              <td>
                                <AmountCell>
                                  ₹
                                  {(
                                    fc.quantity * Number(fc.unit_price)
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                  })}
                                </AmountCell>
                              </td>
                            </>
                          )}
                        </tr>
                      )
                    )}
                  </tbody>
                </PaymentsTable>
              </TableWrap>
            </SectionCard>
          )}

        {quotation.gifts && quotation.gifts.length > 0 && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#e44862">
                <Gift size={18} />
              </CardIcon>
              <div>
                <CardTitle>Gifts</CardTitle>
                <CardSubtitle>Complimentary items</CardSubtitle>
              </div>
            </CardHeader>
            <TableWrap>
              <PaymentsTable $minWidth={360}>
                <thead>
                  <tr>
                    <th>
                      <span>Gift Name</span>
                    </th>
                    <th>
                      <span>Qty</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {quotation.gifts.map((g: any, idx: number) => (
                    <tr key={idx}>
                      <td>{g.gift_name}</td>
                      <td>{g.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </PaymentsTable>
            </TableWrap>
          </SectionCard>
        )}

        {quotation.album_detail && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#c94daa">
                <Layers size={18} />
              </CardIcon>
              <div>
                <CardTitle>Album Details</CardTitle>
                <CardSubtitle>Album specifications</CardSubtitle>
              </div>
            </CardHeader>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Album Type</InfoLabel>
                <InfoValue>
                  {quotation.album_detail.album_type_name}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Width × Height</InfoLabel>
                <InfoValue>
                  {quotation.album_detail.album_width} ×{" "}
                  {quotation.album_detail.album_height} inches
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Album Paper</InfoLabel>
                <InfoValue>
                  {quotation.album_detail.album_paper_name}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Album Pages</InfoLabel>
                <InfoValue>{quotation.album_detail.album_pages}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </SectionCard>
        )}

        {quotation.product_info_description && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#35b7c8">
                <FileText size={18} />
              </CardIcon>
              <div>
                <CardTitle>Product Information</CardTitle>
                <CardSubtitle>Description notes</CardSubtitle>
              </div>
            </CardHeader>
            <NotesBlock>{quotation.product_info_description}</NotesBlock>
          </SectionCard>
        )}

        {hasOptionalServices && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#f09a3e">
                <Layers size={18} />
              </CardIcon>
              <div>
                <CardTitle>Optional Services</CardTitle>
                <CardSubtitle>Add-ons and extras</CardSubtitle>
              </div>
            </CardHeader>
            <InfoGrid>
              {(quotation.optional_services.opt_album_page_cost ||
                quotation.optional_services.opt_album_page_price > 0) && (
                <InfoItem>
                  <InfoLabel>Album Extra Page Sheet</InfoLabel>
                  <InfoValue>
                    {quotation.optional_services.opt_album_page_cost ||
                      "Extra Sheet"}{" "}
                    {quotation.optional_services.opt_album_page_price > 0 &&
                      `(₹${quotation.optional_services.opt_album_page_price})`}
                  </InfoValue>
                </InfoItem>
              )}
              {(quotation.optional_services.opt_video_cost ||
                quotation.optional_services.opt_video_price > 0) && (
                <InfoItem>
                  <InfoLabel>Extra Video</InfoLabel>
                  <InfoValue>
                    {quotation.optional_services.opt_video_cost ||
                      "Extra Video"}{" "}
                    {quotation.optional_services.opt_video_price > 0 &&
                      `(₹${quotation.optional_services.opt_video_price})`}
                  </InfoValue>
                </InfoItem>
              )}
              {(quotation.optional_services.opt_pad_price_from > 0 ||
                quotation.optional_services.opt_pad_price_to > 0) && (
                <InfoItem>
                  <InfoLabel>Album Pad Price Range</InfoLabel>
                  <InfoValue>
                    ₹{quotation.optional_services.opt_pad_price_from || 0} TO ₹
                    {quotation.optional_services.opt_pad_price_to || 0}
                  </InfoValue>
                </InfoItem>
              )}
            </InfoGrid>
          </SectionCard>
        )}

        {quotation.add_terms_conditions !== false && quotation.terms_and_conditions && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#c94daa">
                <FileText size={18} />
              </CardIcon>
              <div>
                <CardTitle>Terms & Conditions</CardTitle>
                <CardSubtitle>Policy terms for this quotation</CardSubtitle>
              </div>
            </CardHeader>
            <NotesBlock style={{ whiteSpace: "pre-wrap" }}>{quotation.terms_and_conditions}</NotesBlock>
          </SectionCard>
        )}

        {!quotation.without_price && (
          <SummaryCard>
            <SummaryRow>
              <span>Discount</span>
              <span>{quotation.discount_percent || 0}%</span>
            </SummaryRow>
            <SummaryRow>
              <span>Tax</span>
              <span>{quotation.tax_percent || 0}%</span>
            </SummaryRow>
            <SummaryTotal>
              <span>Grand Total</span>
              <span>
                ₹
                {calculatedGrandTotal.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </span>
            </SummaryTotal>
          </SummaryCard>
        )}
      </ScrollBody>

      {modalShareQuotation && (
        <QuotationShareModal
          quotation={modalShareQuotation}
          onClose={() => setModalShareQuotation(null)}
          onShareOfficial={handleOfficialShare}
          loading={sharingWhatsApp}
        />
      )}

      <DocumentPrintTemplate
        printRef={printRef}
        activeDoc={
          quotation
            ? { ...quotation, grand_total: String(calculatedGrandTotal) }
            : null
        }
        user={user}
        studioLogo={studio_logo || business?.studio_logo}
        studioName={business?.studio_name}
        studioAddress={business?.address}
        type="quotation"
      />

      {deleteConfirm && (
        <DeletePopup
          text={`Are you sure you want to delete quotation ${quotation.quotation_no}?`}
          buttonText="Delete"
          cancel={() => setDeleteConfirm(false)}
          Delete={handleConfirmDelete}
        />
      )}

      {convertConfirm &&
        createPortal(
          <ModalOverlay onClick={() => !converting && setConvertConfirm(false)}>
            <ModalShell
              style={{ maxWidth: 440 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHero>
                <ModalHeroRow>
                  <ModalHeroText>
                    <ModalHeroBadge>
                      <ArrowRightLeft size={11} />
                      Convert to order
                    </ModalHeroBadge>
                    <ConfirmModalTitle>Convert Quotation?</ConfirmModalTitle>
                    <ModalSubtitle>
                      This will copy all events, resources, and details into an
                      active order.
                    </ModalSubtitle>
                  </ModalHeroText>
                  <CloseBtn
                    type="button"
                    onClick={() => !converting && setConvertConfirm(false)}
                    aria-label="Close"
                  >
                    <X size={18} />
                  </CloseBtn>
                </ModalHeroRow>
              </ModalHero>
              <ModalBody>
                <ConfirmText>
                  Are you sure you want to convert quotation{" "}
                  <strong>{quotation.quotation_no}</strong> into an active
                  order?
                </ConfirmText>
              </ModalBody>
              <ConfirmFooter>
                <CancelBtn
                  type="button"
                  onClick={() => setConvertConfirm(false)}
                  disabled={converting}
                >
                  Cancel
                </CancelBtn>
                <PrimaryBtn
                  type="button"
                  onClick={handleConfirmConvert}
                  disabled={converting}
                >
                  {converting ? (
                    <>
                      <SpinIcon size={16} /> Converting…
                    </>
                  ) : (
                    <>
                      <ArrowRightLeft size={16} /> Convert
                    </>
                  )}
                </PrimaryBtn>
              </ConfirmFooter>
            </ModalShell>
          </ModalOverlay>,
          document.body
        )}
    </PageContainer>
  );
};

export default QuotationDetailsPage;

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  box-sizing: border-box;
  overflow: hidden;
  @media ${device.tablet} {
    padding: 16px;
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -60px;
  right: -4%;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 201, 81, 0.28) 0%,
    transparent 70%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.25;
`;

const PageSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 480px;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  color: rgba(23, 23, 23, 0.72);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
    transform: translateY(-1px);
  }
`;

const ScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 4px;
`;

const SectionCard = styled.div`
  background: #fff;
  border-radius: 20px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CardIcon = styled.div<{ $tone?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone || "#c94daa"};
  background: ${(p) => `${p.$tone || "#c94daa"}14`};
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const CardSubtitle = styled.p`
  margin: 2px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.5);
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.06);
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionDivider = styled.div`
  width: 1px;
  height: 28px;
  background: rgba(23, 23, 23, 0.1);
  flex-shrink: 0;
  @media ${device.tablet} {
    display: none;
  }
`;

const ActionSpacer = styled.div`
  flex: 1;
  min-width: 8px;
`;

const SoftBtn = styled.button<{ $tone: string; $solid?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid
    ${(p) => (p.$solid ? "transparent" : `${p.$tone}33`)};
  background: ${(p) => (p.$solid ? p.$tone : `${p.$tone}14`)};
  color: ${(p) => (p.$solid ? "#fff" : p.$tone)};
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${(p) =>
    p.$solid ? `0 6px 16px ${p.$tone}40` : "none"};
  transition: all 0.15s ease;

  svg {
    flex-shrink: 0;
    display: block;
  }

  &:hover:not(:disabled) {
    background: ${(p) => (p.$solid ? p.$tone : `${p.$tone}22`)};
    border-color: ${(p) => (p.$solid ? "transparent" : `${p.$tone}55`)};
    transform: translateY(-1px);
    box-shadow: ${(p) =>
      p.$solid ? `0 8px 18px ${p.$tone}48` : `0 4px 12px ${p.$tone}18`};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const InfoLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(23, 23, 23, 0.48);
`;

const InfoValue = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #171717;
`;

const NotesBlock = styled.div`
  white-space: pre-wrap;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.78);
  line-height: 1.65;
  padding: 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const SummaryCard = styled.div`
  margin-left: auto;
  width: min(100%, 340px);
  padding: 20px 22px;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.1) 0%,
    rgba(206, 86, 176, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.14);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
`;

const SummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px dashed rgba(201, 77, 170, 0.25);
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #c94daa;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.68);
  line-height: 1.65;
`;

const ConfirmFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 24px 24px;
`;

const CancelBtn = styled.button`
  min-width: 110px;
  height: 46px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  border-radius: 12px;
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.72);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SpinIcon = styled(Loader2)`
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
